<!--
  SoftwareAuthFlhandleAuthenticationCompletedowDialog
  This component generates forms based on backend responses.

  Currently it supports:
  - String inputs (text, password)
  - Enum inputs (select)

  Flow:
  1. Component is created and calls the startAuth method to get an id for the auth flow
  2. The "next_step" is requested from the backend to get the first step of the auth flow
 -->
<template>
  <div v-if="authStatus == 'next_step'" class="dialog-content">
    <!-- Generic form starts here -->
    <!-- Message/Description -->
    <div v-if="stepInformation?.i18n_code" class="step-information">
      {{ i18n.t(stepInformation?.i18n_code) }}
    </div>
    <div v-else-if="stepInformation?.message">
      {{ stepInformation.message }}
    </div>

    <!-- Form -->
    <el-form hide-required-asterisk @submit.prevent>
      <span v-for="field in currentFormFields" :key="field.title">
        <el-form-item :required="field.required">
          <!-- String -->
          <el-input
            v-if="field.type == 'string'"
            v-model="userInput[field.title]"
            :placeholder="getFieldLabel(field.title)"
            :type="isSensitiveField(field.title) ? 'password' : 'text'"
            :show-password="isSensitiveField(field.title)">
          </el-input>

          <!-- Enum -->
          <el-select-v2
            v-else-if="field.type == 'enum'"
            :key="field.title"
            v-model="userInput[field.title]"
            :placeholder="getFieldLabel(field.title)"
            :options="field.availableValues">
          </el-select-v2>
        </el-form-item>
      </span>
    </el-form>
    <!-- Dynamic Error messages for steps are displayed here -->
    <SmDialogMessage
      v-if="inputErrorMessage"
      type="error"
      :visible="inputErrorMessage != null"
      :message="inputErrorMessage" />
    <!-- GENERIC FORM ENDS HERE -->
    <div class="button-wrapper">
      <SmButton
        ref="submitButtonTest"
        :loading="requestLoading"
        class="submit-button"
        @click="goToNextStep()">
        {{ i18n.t('submit') }}
      </SmButton>
    </div>
  </div>
  <div v-else-if="authStatus == 'success'" class="dialog-content">
    <div class="icons-wrapper">
      <img
        :src="`https://logos.saasmetrix.io/${encodeURIComponent(props.applicationName)}.png`"
        class="integration-icon"
        :alt="props.applicationName" />
      <div class="x-wrapper">
        <p class="x">x</p>
      </div>
      <img
        src="https://logos.saasmetrix.io/Logo_Icon_light.svg"
        class="integration-icon"
        :alt="i18n.t('saasmetrix')" />
    </div>
    {{
      i18n.t('dialogs.authFlowDialog.successText', {
        softwareName: applicationName,
      })
    }}
  </div>
  <div v-else-if="authStatus == 'error'" class="dialog-content">
    <SmDialogMessage
      :visible="authStatus == 'error'"
      type="error"
      :message="
        i18n.t('dialogs.authFlowDialog.failedText', {
          softwareName: applicationName,
        })
      " />
    <div class="button-wrapper">
      <SmButton :loading="requestLoading" @click="startAuth()">
        {{ i18n.t('dialogs.authFlowDialog.tryAgain') }}
      </SmButton>
    </div>
  </div>
  <!-- CONTENT ENDS HERE-->
</template>

<script setup lang="ts">
  // Imports
  import * as Sentry from '@sentry/vue'
  import { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
  import JsonRefs from 'json-refs'
  import { useI18n } from 'vue-i18n'

  import {
    SoftwareAuthorizationOutError,
    SoftwareAuthorizationOutNextStep,
    SoftwareAuthorizationService,
    SoftwareTypes,
  } from '@/client'
  import { AuthType } from '@/stores/applicationReauthStore'
  import { sendToast } from '@/components/sm/SmNotification'

  const i18n = useI18n()

  // Types
  type inputFieldInformationTypes = 'string' | 'enum'

  type inputInformationBase = {
    title: string
    type: inputFieldInformationTypes
    required: boolean
  }

  type textInputField = inputInformationBase & { type: 'string' }
  type enumInputField = inputInformationBase & {
    type: 'enum'
    availableValues: OptionType[]
  }
  type InputInformation = textInputField | enumInputField

  type LogLevel = 'error' | 'warning' | 'info'

  // Emits
  const emit = defineEmits(['update:visibility', 'authentication-completed'])

  // Props
  const props = defineProps<{
    applicationName: string
    applicationId?: string
    applicationType: SoftwareTypes
    authType: AuthType
  }>()

  /*
  Defining Constants

  authorizationId: Every authorization process has an id, that is used to identify the process. The id is generated by the backend.
  authStatus: The status of the authorization process. Can be 'next_step', 'success' or 'error'.
  next_step: The authorization process is not finished yet, the user has to provide more information in a next step.
  success: The authorization process is finished successfully.
  error: The authorization process failed.
  stepInformation: The information about the current step. This is a SoftwareAuthorizationOutNextStep object.
  stepInformationData: Information about the form fields of the current step. A parsed version of stepInformation.data.
  */
  const requestLoading = ref(false)
  const authorizationId = ref<string>() // this is the id of the authorization process
  const authStatus = ref<'success' | 'error' | 'next_step'>('next_step')
  const stepInformation = ref<SoftwareAuthorizationOutNextStep>()
  const stepInformationData = ref()

  // Input-Step related Data
  const inputErrorMessage = ref<string | null>(null)
  const userInput = ref<Record<string, string>>({})

  const currentFormFields: Ref<InputInformation[]> = ref([])
  watch(
    () => stepInformationData.value,
    () => {
      const schema = stepInformationData.value
      if (!schema || !schema.properties) return
      currentFormFields.value = []

      for (const [key, value] of Object.entries(schema.properties)) {
        // Skip unresolved $ref entries; they will be handled once JsonRefs finishes resolving
        if (
          value &&
          typeof value === 'object' &&
          '$ref' in value &&
          !('type' in value) &&
          !('enum' in value) &&
          !('const' in value)
        ) {
          continue
        }

        try {
          const field = stepInformationDataToCurrentFormField(key, value)
          currentFormFields.value.push(field)
        } catch (e) {
          logError({
            consoleMessage:
              'Error while parsing stepInformationData in AuthFlowContent',
            sentryMessage:
              'Error while parsing stepInformationData in AuthFlowContent',
            level: 'warning',
            toastMessage: i18n.t('error.general.unknown_error.message'),
            extra: {
              applicationName: props.applicationName,
              applicationType: props.applicationType,
              authType: props.authType,
            },
          })
        }
      }
    }
  )

  // On Setup
  startAuth()

  document.addEventListener('keypress', (event) => {
    if (event.key === 'Enter') {
      goToNextStep()
    }
  })

  // ********************************* Request Functions *********************************

  // Start the authorization process
  // This function is called on component creation
  // Get the authorization id and start the first step (call goToNextStep)
  function startAuth() {
    requestLoading.value = true
    SoftwareAuthorizationService.startAuthorizeSoftwareApiV1SoftwareAuthorizeStartPost(
      {
        requestBody: {
          software_name: props.applicationName,
          software_type: props.applicationType,
          software_id: props.applicationId,
        },
      }
    ).then(
      (res) => {
        authorizationId.value = res
        authStatus.value = 'next_step'
        requestLoading.value = false
        // After the id has been crawled, the first step is initiated
        goToNextStep()
      },
      // rejected part
      () => {
        requestLoading.value = false
        authStatus.value = 'error'
        inputErrorMessage.value = i18n.t(
          'error.general.unknown_error.message'
        ) as string

        logError({
          consoleMessage: 'Auth flow start failed',
          sentryMessage: 'Auth flow start failed',
          level: 'error',
          toastMessage: i18n.t('notifications.authorize.connectError.message'),
          extra: {
            applicationName: props.applicationName,
            applicationType: props.applicationType,
            authType: props.authType,
          },
        })
      }
    )
  }

  function goToNextStep() {
    const userInputKeys = Object.keys(userInput.value || {})
    if (
      userInputKeys.includes('magic_link_or_mfa_code') ||
      userInputKeys.includes('mfa_code')
    ) {
      Sentry.captureMessage('MFA auth submission', {
        level: 'info',
        extra: {
          applicationName: props.applicationName,
          applicationType: props.applicationType,
          authType: props.authType,
          authorizationId: authorizationId.value,
          userInputKeys: userInputKeys,
        },
      })
    }

    if (!authorizationId.value) {
      authStatus.value = 'error'
      return
    }

    requestLoading.value = true
    SoftwareAuthorizationService.authorizeSoftwareStepApiV1SoftwareAuthorizeStepPost(
      {
        requestBody: {
          software_authorization_id: authorizationId.value,
          user_input: userInput.value,
        },
      }
    )
      .then(
        async (res) => {
          switch (res.type) {
            case 'success': {
              authStatus.value = 'success'
              // software get's authenticated with the id
              switch (props.authType) {
                case 'authenticate':
                  SoftwareAuthorizationService.postSoftwareApiV1SoftwareSoftwarePost(
                    {
                      requestBody: {
                        software_name: props.applicationName,
                        type: props.applicationType,
                        software_authorization_id: authorizationId.value,
                      },
                    }
                  ).then(
                    () => {
                      // dialog closes automatically
                      setTimeout(() => {
                        emit('authentication-completed')
                      }, 1500)
                    },
                    () => {
                      authStatus.value = 'error'
                      inputErrorMessage.value = i18n.t(
                        'error.general.unknown_error.message'
                      ) as string

                      logError({
                        consoleMessage:
                          'Auth flow finalization failed (post software)',
                        sentryMessage:
                          'Auth flow finalization failed (post software)',
                        level: 'error',
                        toastMessage: inputErrorMessage.value,
                        extra: {
                          applicationName: props.applicationName,
                          applicationType: props.applicationType,
                          authType: props.authType,
                          authorizationId: authorizationId.value,
                        },
                      })
                    }
                  )
                  break
                case 'reauthenticate':
                  if (!props.applicationId) {
                    logError({
                      consoleMessage:
                        'Error while reauthenticating software in AuthFlowContent, ID is MISSING as Prop to SoftwareAuthFlowContent',
                      sentryMessage:
                        'Error while reauthenticating software in AuthFlowContent, ID is MISSING as Prop to SoftwareAuthFlowContent',
                      level: 'error',
                      toastMessage: i18n.t(
                        'error.general.unknown_error.message'
                      ),
                      extra: {
                        applicationName: props.applicationName,
                        applicationType: props.applicationType,
                        authType: props.authType,
                      },
                    })
                    return
                  }
                  try {
                    await SoftwareAuthorizationService.reauthorizeSoftwareApiV1SoftwareSoftwareIdReauthorizePost(
                      {
                        softwareId: props.applicationId,
                        requestBody: {
                          software_authorization_id: authorizationId.value,
                        },
                      }
                    )

                    emit('authentication-completed')
                  } catch {
                    authStatus.value = 'error'
                    inputErrorMessage.value = i18n.t(
                      'error.general.unknown_error.message'
                    ) as string

                    logError({
                      consoleMessage:
                        'Auth flow finalization failed (reauthorize software)',
                      sentryMessage:
                        'Auth flow finalization failed (reauthorize software)',
                      level: 'error',
                      toastMessage: inputErrorMessage.value,
                      extra: {
                        applicationName: props.applicationName,
                        applicationType: props.applicationType,
                        authType: props.authType,
                        authorizationId: authorizationId.value,
                        applicationId: props.applicationId,
                      },
                    })
                  }
              }
              break
            }

            case 'error': {
              const error = res as SoftwareAuthorizationOutError
              inputErrorMessage.value =
                error.error_message ||
                i18n.t('error.general.unknown_error.message')

              logError({
                consoleMessage: 'Auth flow step error',
                sentryMessage: 'Auth flow step error',
                level: 'warning',
                toastMessage: inputErrorMessage.value as string,
                extra: {
                  applicationName: props.applicationName,
                  applicationType: props.applicationType,
                  authType: props.authType,
                  authorizationId: authorizationId.value,
                  errorMessage: error.error_message,
                  errorCode: error.error_code,
                  userInputKeys: Object.keys(userInput.value || {}),
                  stepTitle: stepInformation.value?.title,
                },
              })
              break
            }
            case 'next_step': {
              const castedResult = res as SoftwareAuthorizationOutNextStep
              inputErrorMessage.value = null
              stepInformation.value = castedResult

              if (!castedResult.data || typeof castedResult.data !== 'object') {
                authStatus.value = 'error'
                return
              }

              // Use raw data immediately and try to resolve refs in the background.
              stepInformationData.value = castedResult.data

              // parsing the json scheme data, so that $refs (json scheme pointers, that point to other definition locations) are resolved automatically
              JsonRefs.resolveRefs(castedResult.data, {
                resolveCirculars: false,
              })
                .then((resolvedData) => {
                  // Update only if resolution succeeded
                  stepInformationData.value = resolvedData.resolved
                })
                .catch((err) => {
                  // Fall back to manual resolution
                  try {
                    const manuallyResolved = resolveRefs(
                      castedResult.data!,
                      castedResult.data!.$defs
                    )
                    stepInformationData.value = manuallyResolved
                  } catch (manualErr) {
                    // Fall back to the raw schema so fields still render
                    Sentry.captureException(err)
                    stepInformationData.value = castedResult.data!
                  }
                })
              break
            }
          }
        },
        // rejected callback
        () => {
          authStatus.value = 'error'
          inputErrorMessage.value = i18n.t(
            'error.general.unknown_error.message'
          ) as string

          logError({
            consoleMessage: 'Auth flow step request failed',
            sentryMessage: 'Auth flow step request failed',
            level: 'error',
            toastMessage: i18n.t('error.general.unknown_error.message'),
            extra: {
              applicationName: props.applicationName,
              applicationType: props.applicationType,
              authType: props.authType,
              authorizationId: authorizationId.value,
              userInputKeys: Object.keys(userInput.value || {}),
            },
          })
        }
      )
      .finally(() => {
        userInput.value = {}
        requestLoading.value = false
      })
  }

  // ********************************* Helper Functions *********************************

  function normalizeFieldTitle(fieldTitle: string): string {
    return fieldTitle.toLowerCase().replace(/[^a-z0-9]/g, '')
  }

  function isSensitiveField(fieldTitle: string): boolean {
    const normalizedFieldTitle = normalizeFieldTitle(fieldTitle)
    return normalizedFieldTitle === 'password'
  }

  function getFieldTranslationKey(fieldTitle: string): string | null {
    if (i18n.te(fieldTitle)) {
      return fieldTitle
    }

    const scopedTranslationKey = `integration.authorization.${fieldTitle}`
    if (i18n.te(scopedTranslationKey)) {
      return scopedTranslationKey
    }

    return null
  }

  function getFieldLabel(fieldTitle: string): string {
    const translationKey = getFieldTranslationKey(fieldTitle)
    if (translationKey) {
      return i18n.t(translationKey) as string
    }

    return fieldTitle
  }

  // Helper function to manually resolve $refs in the schema
  function resolveRefs(schema: any, defs: any): any {
    if (!schema || typeof schema !== 'object') return schema

    // If this is a $ref, resolve it
    if (schema.$ref) {
      const refPath = schema.$ref
      if (refPath.startsWith('#/$defs/')) {
        const defName = refPath.replace('#/$defs/', '')
        if (defs && defs[defName]) {
          return resolveRefs(defs[defName], defs)
        }
      }
    }

    // Recursively resolve refs in properties
    const resolved: any = {}
    for (const [key, value] of Object.entries(schema)) {
      if (key === '$defs') {
        resolved[key] = value
      } else {
        resolved[key] = resolveRefs(value, defs || schema.$defs)
      }
    }

    return resolved
  }

  function logError(options: {
    consoleMessage: string
    sentryMessage: string
    level?: LogLevel
    toastMessage: string | unknown
    extra?: Record<string, unknown>
  }) {
    const level = options.level || 'error'

    console.error(options.consoleMessage, options.extra)

    Sentry.captureMessage(options.sentryMessage, {
      level: level,
      extra: options.extra,
    })

    sendToast(
      i18n.t('notifications.authorize.connectError.title'),
      String(options.toastMessage),
      level === 'error' ? 'error' : 'info'
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function stepInformationDataToCurrentFormField(key: string, value: any) {
    // Determine the type - use enum if present, otherwise use the type field
    const type = value.enum ? 'enum' : value.type

    const requiredFields = Array.isArray(stepInformationData.value.required)
      ? stepInformationData.value.required
      : []
    const isRequired = requiredFields.includes(key)

    if (value.const) {
      const item = value.const
      return {
        title: key,
        type: 'enum',
        required: isRequired as boolean,
        availableValues: [
          { label: i18n.te(item) ? i18n.t(item) : item, value: value.const },
        ],
      } as enumInputField
    }

    switch (type) {
      case 'string':
        return {
          title: key,
          type: 'string',
          required: isRequired as boolean,
        } as textInputField
      case 'enum':
        // Check if description is available
        if (value.description) {
          // Parse Description to get labels
          // Example: description: "User profiles:\n* `null` - Personal Account\n* `11EC180B4A82421E985D772BB5DD70C8` - First Company\n* `11EC180B4A82421E985D772BB5DD70AB` - Second company"
          const description = value.description

          // Remove prefix
          const descriptionWithoutPrefix = description.replace(
            'User profiles:\n',
            ''
          )

          // Split by newline
          const descriptionSplitByNewline = descriptionWithoutPrefix.split('\n')

          // Remove empty lines
          const descriptionWithoutEmptyLines = descriptionSplitByNewline.filter(
            (line: string) => line != ''
          )

          // Remove bullet points
          const descriptionWithoutBulletPoints =
            descriptionWithoutEmptyLines.map((line: string) =>
              line.replace('* ', '')
            )

          // Split by `-`
          const descriptionSplitByDash = descriptionWithoutBulletPoints.map(
            (line: string) => line.split(' - ')
          )

          // Remove ``
          descriptionSplitByDash.forEach((line: string[]) => {
            line[0] = line[0].replaceAll('`', '').replaceAll("'", '')
          })

          // Create availableValues
          const availableValues = descriptionSplitByDash.map(
            (line: string[]) => {
              return {
                label: i18n.te(line[1]) ? i18n.t(line[1]) : line[1],
                value: line[0],
              }
            }
          )

          return {
            title: key,
            type: 'enum',
            required: isRequired as boolean,
            availableValues: availableValues,
          } as enumInputField
        }

        // Handle enum without description
        // Note: value.enum might be a Vue Proxy, so we need to check it carefully
        if (!value.enum) {
          console.error('Invalid enum for field:', key, value)
          Sentry.captureMessage('Auth flow enum field invalid', {
            level: 'warning',
            extra: { field: key, value: value },
          })
          return {
            title: key,
            type: 'string',
            required: isRequired as boolean,
          } as textInputField
        }

        // Convert to array if needed (Vue Proxy can be tricky)
        const enumArray = Array.isArray(value.enum)
          ? value.enum
          : Object.values(value.enum)
        if (enumArray.length === 0) {
          console.error('Invalid enum for field:', key, value)
          Sentry.captureMessage('Auth flow enum field empty', {
            level: 'warning',
            extra: { field: key, value: value },
          })
          return {
            title: key,
            type: 'string',
            required: isRequired as boolean,
          } as textInputField
        }

        return {
          title: key,
          type: 'enum',
          required: isRequired as boolean,
          availableValues: enumArray.map((item: string) => ({
            label: i18n.te(item) ? i18n.t(item) : item,
            value: item.replace('`', '').replace("'", ''),
          })),
        } as enumInputField
    }

    Sentry.captureMessage('Auth flow unknown field type', {
      level: 'warning',
      extra: {
        field: key,
        value: value,
      },
    })

    return {
      title: key,
      type: 'string',
      required: isRequired as boolean,
    } as textInputField
  }
</script>

<style scoped lang="scss">
  .step-information {
    text-align: left;
  }

  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1rem;
  }

  .application-image {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
  }

  .dialog-content {
    gap: 1rem;
    display: flex;
    flex-direction: column;
  }

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
  }

  .error-message {
    font-size: 14px;
    color: var(--sm-magenta);
  }

  .x {
    font-size: 2.5rem;
    margin: 0;
    vertical-align: middle;
  }

  .x-wrapper {
    margin-left: 1rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--el-text-color-disabled);
  }

  .integration-icon {
    height: 4rem;
    width: 4rem;
  }

  .icons-wrapper {
    display: flex;
    width: 100%;
    justify-content: center;
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
</style>
